import { useContext, useState } from 'react';
import { CompanyContext } from '../../../../context/CompanyContext';
import { CsvUploadConfiguration, CsvUploadTemplate, CsvUploadType } from '../../../../types/csvUploadConfiguration';
import { useLazyQuery } from '@apollo/client';
import {
  CsvUploadConfigurationQueries,
  GetCsvUploadConfigurationForFileResponse,
  GetCsvUploadConfigurationForFileVariables,
} from '../../../../graphql/csvUploadConfiguration.graphql';
import { Base64 } from 'js-base64';
import React from 'react';
import Modal, { ModalPane } from '../../../../VentoryUI/components/common/Modal';
import { Grid } from '@mui/material';
import { t } from '../../../../types/translation/Translator';
import { TranslationKey } from '../../../../types/translation/TranslationKey';
import UploadCsvInput from '../../Common/UploadCsvInput';
import { CustomFieldContext } from '../../../../context/CustomFieldContext';
import CreateCsvUploadConfigurationMapping from '../../../components/CsvUploadConfiguration/CreateCsvUploadConfigurationMapping';
import { CsvUploadConfigurationSelect } from '../../../components/CsvUploadConfiguration/CsvUploadConfigurationSelect';
import CsvUploadConfigurationInspect from '../../../components/CsvUploadConfiguration/CsvUploadConfigurationInspect';
import { ButtonVariant, Button, CancelButton, NextButton } from '../../../components/Button';

enum AssetImportStatus {
  fileUpload,
  selectConfig,
  createConfig,
  inspect,
}

interface AssetImportModalInputProps {
  open: boolean;
  setOpen: (value: boolean) => void;
}

export default function AssetImportModal({ open, setOpen }: AssetImportModalInputProps) {
  const { currentCompany } = useContext(CompanyContext);
  const { customFields } = useContext(CustomFieldContext);

  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<AssetImportStatus>(AssetImportStatus.fileUpload);

  const [configurations, setConfigurations] = useState<CsvUploadConfiguration[]>([]);
  const [otherConfigurations, setOtherConfigurations] = useState<CsvUploadConfiguration[]>([]);
  const [fileHeaders, setFileHeaders] = useState<string[]>([]);
  const [requiredHeaders, setRequiredHeaders] = useState<string[]>([]);
  const [optionalHeaders, setOptionalHeaders] = useState<string[]>([]);
  const [fileAsBase64, setFileAsBase64] = useState<string | null>(null);
  const [deleteExisting, setDeleteExisting] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [inspectId, setInspectId] = useState<string>();

  const [getConfigurations, { loading: getConfigurationsLoading }] = useLazyQuery<
    GetCsvUploadConfigurationForFileResponse,
    GetCsvUploadConfigurationForFileVariables
  >(CsvUploadConfigurationQueries.getForFile, {
    onCompleted: res => {
      setConfigurations(res.csvConfigurationsForFile.configurations.map(c => new CsvUploadConfiguration(c)));
      setFileHeaders(res.csvConfigurationsForFile.fileHeaders);

      const forFile = res.csvConfigurationsForFile;
      setRequiredHeaders(forFile.requiredHeaders);
      setOptionalHeaders(forFile.optionalHeaders);

      setOtherConfigurations(res.csvConfigurationsForFile.otherConfigurations);
      if (
        !res.csvConfigurationsForFile.configurations.length &&
        !res.csvConfigurationsForFile.otherConfigurations.length
      ) {
        setStatus(AssetImportStatus.createConfig);
      } else {
        setStatus(AssetImportStatus.selectConfig);
      }
    },
    onError: err => setError(err.message),
  });

  const handleFile = (files: FileList | null) => {
    if (!files) {
      setFile(null);
      return;
    }
    setFile(files[0]);
  };

  const handleFileUpload = async () => {
    if (!file) return;

    let asBase64 = Base64.encode(await file.text());

    await getConfigurations({
      variables: {
        companyId: currentCompany.id,
        type: CsvUploadType.asset,
        csvFileAsBase64Data: asBase64,
      },
    });
    setFileAsBase64(asBase64);
  };

  const handleClose = () => {
    setFile(null);
    setStatus(AssetImportStatus.fileUpload);
    setConfigurations([]);
    setOtherConfigurations([]);
    setFileHeaders([]);
    setRequiredHeaders([]);
    setOptionalHeaders([]);
    setFileAsBase64(null);
    setOpen(false);
    setDeleteExisting(false);
    setError('');
  };

  const handleBack = () => {
    setError('');
    switch (status) {
      case AssetImportStatus.fileUpload:
        return;
      case AssetImportStatus.selectConfig:
        return setStatus(AssetImportStatus.fileUpload);
      case AssetImportStatus.createConfig:
        return setStatus(AssetImportStatus.fileUpload);
      case AssetImportStatus.inspect:
        return setStatus(AssetImportStatus.selectConfig);
    }
  };

  const content = () => {
    if (status === AssetImportStatus.createConfig && fileAsBase64) {
      return (
        <CreateCsvUploadConfigurationMapping
          handleUploadError={newConfiguration => {
            if (newConfiguration) setConfigurations([...configurations, newConfiguration]);
            setStatus(AssetImportStatus.selectConfig);
          }}
          onClose={handleClose}
          onBack={handleBack}
          type={CsvUploadType.asset}
          required={requiredHeaders}
          optional={optionalHeaders}
          headers={fileHeaders}
          csvFileAsBase64Data={fileAsBase64}
          setError={setError}
        />
      );
    }

    if (status === AssetImportStatus.selectConfig && fileAsBase64) {
      return (
        <CsvUploadConfigurationSelect
          otherConfigurations={otherConfigurations}
          deleteExisting={deleteExisting}
          onClose={handleClose}
          onCreate={() => setStatus(AssetImportStatus.createConfig)}
          onBack={handleBack}
          csvFileAsBase64Data={fileAsBase64}
          type={CsvUploadType.asset}
          configurations={configurations}
          setError={setError}
          onInspect={id => {
            setInspectId(id);
            setStatus(AssetImportStatus.inspect);
          }}
        />
      );
    }

    if (status === AssetImportStatus.inspect) {
      return (
        <CsvUploadConfigurationInspect
          headers={fileHeaders}
          configuration={otherConfigurations.find(c => c.id === inspectId)}
          onBack={handleBack}
        />
      );
    }

    return (
      <ModalPane
        footer={
          <div className='flex justify-between gap-2'>
            <a
              style={{ textDecoration: 'none' }}
              href={`${process.env.REACT_APP_STORAGE_URL}/ventory/${CsvUploadTemplate.asset}`}
              download={CsvUploadTemplate.asset}
            >
              <Button
                variant={ButtonVariant.secondary}
                onClick={() => {}}
                text={t(TranslationKey.downloadTemplate).singular.label}
              />
            </a>
            <div className='flex gap-2'>
              <CancelButton disabled={getConfigurationsLoading} onClick={handleClose} />
              <NextButton disabled={!file} onClick={handleFileUpload} loading={getConfigurationsLoading} />
            </div>
          </div>
        }
      >
        <Grid container height={'100%'} alignContent={'space-between'}>
          <Grid item xs={12}>
            <Grid container rowSpacing={1}>
              <Grid item xs={12}>
                <UploadCsvInput file={file} onFile={handleFile} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </ModalPane>
    );
  };

  return (
    <Modal
      open={open}
      error={error}
      onClose={handleClose}
      width='70%'
      height='650px'
      title={t(TranslationKey.importAssets).singular.label}
    >
      {content()}
    </Modal>
  );
}
